import React, { useMemo } from "react";
import { Globe } from "lucide-react";
import ThreatMap from "./ThreatMap";
import type { NationThreat } from "../../types/dashboard";

interface MostAffectedNationsProps {
  nations?: NationThreat[];
  timeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  loading?: boolean;
  isValidating?: boolean;
  error?: string | null;
}

export default function MostAffectedNationsSection({
  nations = [],
  timeframe = "30d",
  onTimeframeChange,
  loading = false,
  isValidating = false,
  error = null,
}: MostAffectedNationsProps) {
  return (
    <div className="dash-card flex flex-col relative min-h-[560px]">
      <div className="dash-card-header mb-4 flex justify-between items-center z-10 shrink-0">
        <h2 className="dash-card-title flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#6366F1]" />
          <span>Most affected nations</span>
          {isValidating && !loading && (
            <div className="flex items-center gap-1.5 ml-3 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] text-slate-500 dark:text-slate-400 font-medium">
              <div className="w-3 h-3 border-2 border-slate-300 dark:border-slate-500 border-t-[#6366F1] dark:border-t-[#6366F1] rounded-full animate-spin"></div>
              <span>Live</span>
            </div>
          )}
        </h2>
        <select
          value={timeframe}
          onChange={(e) => onTimeframeChange?.(e.target.value)}
          className="dash-select-sm"
          disabled={loading}
        >
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="3m">Last 3 months</option>
          <option value="6m">Last 6 months</option>
        </select>
      </div>

      <div className="flex-1 w-full h-full relative border border-slate-200 dark:border-white/10 rounded overflow-hidden flex flex-col min-h-[480px]">
        {/* Thin top progress bar for background fetching */}
        {isValidating && !loading && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-800 z-50 overflow-hidden">
            <div className="h-full bg-sky-500 w-1/3 animate-[slide_1.5s_ease-in-out_infinite]" style={{ animation: "slide 1.5s ease-in-out infinite" }}></div>
          </div>
        )}

        {error ? (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-50 dark:bg-[#0b1329]">
            <div className="text-red-500 text-sm mb-2">{error}</div>
            <button
              onClick={() => onTimeframeChange?.(timeframe)}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded text-xs hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-50 dark:bg-[#0b1329]">
            <div className="w-8 h-8 border-4 border-slate-300 dark:border-slate-700 border-t-sky-500 rounded-full animate-spin"></div>
          </div>
        ) : nations.length === 0 ? (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-50 dark:bg-[#0b1329]">
            <div className="text-slate-500 dark:text-slate-400 text-sm">
              {timeframe === "24h"
                ? "No geographic threat data available for the last 24 hours."
                : "No attack data for this period."}
            </div>
          </div>
        ) : null}
        
        {/* Dim the map slightly when fetching new data */}
        <div className={`w-full h-full transition-opacity duration-300 ${isValidating && !loading ? 'opacity-70' : 'opacity-100'}`}>
          <ThreatMap nations={nations} timeframe={timeframe} loading={loading} />
        </div>
      </div>
    </div>
  );
}
